import { useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";

/**
 * Forgot-password page (Phase 1A).
 *
 * Calls POST /api/auth/forgot-password. Backend always returns 200 to prevent
 * email enumeration. In Phase 1A (no email integration yet) the backend returns
 * a `token` in the response body for direct use — once Phase 1D adds email
 * sending, that token will only arrive in the user's inbox.
 *
 * For now we show the reset link inline so admins can demo the flow.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setResult(data);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "Could not process request / Request process nahi ho payi"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const resetUrl = result?.token ? `/reset-password?token=${result.token}` : null;

  return (
    <div
      data-testid="forgot-password-page"
      className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 mb-3">
            <div className="h-12 w-12 rounded-md bg-white border border-slate-200 flex items-center justify-center p-1">
              <img src="/bravura_logo.png" alt="Bravura" className="h-full w-full object-contain" />
            </div>
            <div className="text-left">
              <div className="text-xs uppercase tracking-widest text-slate-500">Bravura</div>
              <div className="font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
                HR Portal
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-7">
          <h1 className="text-2xl font-bold text-slate-900 mb-1" style={{ fontFamily: "Chivo" }}>
            Forgot password
          </h1>
          <p className="text-sm text-slate-500 mb-6">
            Enter your registered email — we'll send you a reset link.
            <br />
            <span className="text-slate-400">
              Apna registered email daalein — reset link mil jayega.
            </span>
          </p>

          {!result && (
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-xs uppercase tracking-wider text-slate-500">
                  Email
                </Label>
                <Input
                  id="email"
                  data-testid="forgot-email-input"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1.5"
                  placeholder="you@bravurapackers.com"
                />
              </div>

              {error && (
                <div
                  data-testid="forgot-error"
                  className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                >
                  {error}
                </div>
              )}

              <Button
                type="submit"
                data-testid="forgot-submit-btn"
                disabled={submitting}
                className="w-full brand-bg brand-bg-hover text-white font-medium"
              >
                {submitting ? "Sending..." : "Send reset link"}
              </Button>
            </form>
          )}

          {result && (
            <div
              data-testid="forgot-success"
              className="space-y-4"
            >
              <div className="text-sm bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-md px-3 py-3">
                <div className="font-semibold mb-1">Request submitted ✓</div>
                <div>
                  If the email exists, a reset link has been sent. Link expires in 15 minutes.
                  <br />
                  <span className="text-emerald-700/80">
                    Agar email register hai to reset link bhej diya gaya hai. 15 min me expire ho jayega.
                  </span>
                </div>
              </div>

              {resetUrl && (
                <div className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-md px-3 py-3">
                  <div className="font-semibold mb-1">Dev mode — email delivery comes in Phase 1D</div>
                  <Link
                    to={resetUrl}
                    data-testid="forgot-direct-link"
                    className="break-all underline font-mono"
                  >
                    Open reset link
                  </Link>
                </div>
              )}
            </div>
          )}

          <div className="mt-6 text-center">
            <Link
              to="/login"
              data-testid="forgot-back-to-login"
              className="text-xs text-slate-500 hover:text-slate-700"
            >
              ← Back to login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
